/**
 * Modal pour configurer la ventilation du budget par catégorie
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Trash2, Save } from 'lucide-react';
import { formatMontant } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type {
  BudgetVentile,
  BudgetCategoryComparison,
  BudgetCategory,
} from '@/types/budgetVentile.types';
import { CATEGORIES_BUDGET } from '@/types/budgetVentile.types';
import { useModalProtection } from '@/hooks/useModalProtection';

// =============================================================================
// PROPS
// =============================================================================

interface BudgetVentilationModalProps {
  isOpen: boolean;
  onClose: () => void;
  budgetTotal: number;
  currentVentilation: BudgetVentile | null;
  depensesActuelles?: Record<BudgetCategory, BudgetCategoryComparison> | null;
  onSave: (ventilation: BudgetVentile) => Promise<boolean>;
  onClear?: () => Promise<boolean>;
}

// =============================================================================
// COMPOSANT
// =============================================================================

export function BudgetVentilationModal({
  isOpen,
  onClose,
  budgetTotal,
  currentVentilation,
  depensesActuelles,
  onSave,
  onClear,
}: BudgetVentilationModalProps) {
  useModalProtection(isOpen);

  // État local du formulaire
  const [ventilation, setVentilation] = useState<BudgetVentile>({
    main_oeuvre: 0,
    materiaux: 0,
    sous_traitance: 0,
    frais_annexes: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialiser avec les valeurs actuelles
  useEffect(() => {
    if (currentVentilation) {
      setVentilation(currentVentilation);
    } else {
      setVentilation({
        main_oeuvre: 0,
        materiaux: 0,
        sous_traitance: 0,
        frais_annexes: 0,
      });
    }
  }, [currentVentilation, isOpen]);

  // Calculs
  const totalVentile = useMemo(() => {
    return (
      ventilation.main_oeuvre +
      ventilation.materiaux +
      ventilation.sous_traitance +
      ventilation.frais_annexes
    );
  }, [ventilation]);

  const ecartTotal = budgetTotal - totalVentile;
  const hasEcart = Math.abs(ecartTotal) > 0.01;
  const isOverBudget = ecartTotal < 0;
  const isValid = !isOverBudget && totalVentile > 0;

  // Mettre à jour une catégorie
  const updateCategory = (key: BudgetCategory, value: string) => {
    const numValue = parseFloat(value) || 0;
    setVentilation((prev) => ({
      ...prev,
      [key]: Math.max(0, numValue),
    }));
  };

  // Sauvegarder
  const handleSave = async () => {
    if (!isValid) return;

    setIsSubmitting(true);
    const success = await onSave(ventilation);
    setIsSubmitting(false);

    if (success) {
      onClose();
    }
  };

  // Supprimer la ventilation
  const handleClear = async () => {
    if (!onClear) return;

    setIsSubmitting(true);
    const success = await onClear();
    setIsSubmitting(false);

    if (success) {
      onClose();
    }
  };

  // Répartir uniformément
  const handleDistributeEvenly = () => {
    const perCategory = Math.floor(budgetTotal / 4);
    const remainder = budgetTotal - perCategory * 4;
    setVentilation({
      main_oeuvre: perCategory + remainder, // Ajouter le reste à la première
      materiaux: perCategory,
      sous_traitance: perCategory,
      frais_annexes: perCategory,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Ventilation du budget</DialogTitle>
          <DialogDescription className="space-y-2">
            <span className="block">
              Répartissez le budget total HT de{' '}
              <span className="font-semibold text-foreground">
                {formatMontant(budgetTotal)}
              </span>{' '}
              entre les catégories de dépenses.
            </span>
            <span className="block text-xs text-muted-foreground/80 mt-1 break-words">
              Cela vous permettra de suivre chaque poste séparément et d'être alerté
              si l'un d'eux dépasse son budget, même si le budget global est respecté.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Bouton répartition uniforme */}
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDistributeEvenly}
            >
              Répartir uniformément
            </Button>
          </div>

          {/* Champs par catégorie */}
          {CATEGORIES_BUDGET.map((cat) => {
            const depense = depensesActuelles?.[cat.key];
            return (
              <div key={cat.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor={`budget-${cat.key}`}
                    className="flex items-center gap-2"
                  >
                    <span className="text-lg">{cat.icon}</span>
                    <span>{cat.label}</span>
                  </Label>
                  {depense && (
                    <span className="text-xs text-muted-foreground">
                      Dépensé : {formatMontant(depense.reel)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    id={`budget-${cat.key}`}
                    type="number"
                    min="0"
                    step="100"
                    value={ventilation[cat.key] || ''}
                    onChange={(e) => updateCategory(cat.key, e.target.value)}
                    placeholder="0"
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground w-8">HT</span>
                </div>
                {/* Barre de suggestion si dépense > budget */}
                {depense && ventilation[cat.key] > 0 && depense.reel > ventilation[cat.key] && (
                  <p className="text-xs text-red-500">
                    Les dépenses actuelles ({formatMontant(depense.reel)}) dépassent déjà ce budget
                  </p>
                )}
              </div>
            );
          })}

          {/* Récapitulatif */}
          <div className="border-t pt-4 mt-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total ventilé :</span>
              <span
                className={cn(
                  'font-bold text-lg',
                  isOverBudget
                    ? 'text-red-500'
                    : hasEcart
                    ? 'text-amber-500'
                    : 'text-emerald-500'
                )}
              >
                {formatMontant(totalVentile)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Budget total HT :</span>
              <span className="font-medium">{formatMontant(budgetTotal)}</span>
            </div>

            {hasEcart && (
              <div
                className={cn(
                  'flex items-start gap-2 p-3 rounded-lg',
                  isOverBudget
                    ? 'bg-red-500/10 border border-red-500/20'
                    : 'bg-amber-500/10 border border-amber-500/20'
                )}
              >
                <AlertCircle
                  className={cn(
                    'h-4 w-4 mt-0.5 flex-shrink-0',
                    isOverBudget ? 'text-red-500' : 'text-amber-500'
                  )}
                />
                <p
                  className={cn(
                    'text-sm',
                    isOverBudget ? 'text-red-600' : 'text-amber-600'
                  )}
                >
                  {isOverBudget
                    ? `Le total dépasse le budget de ${formatMontant(Math.abs(ecartTotal))}`
                    : `Il reste ${formatMontant(ecartTotal)} non ventilé`}
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {currentVentilation && onClear && (
            <Button
              type="button"
              variant="outline"
              onClick={handleClear}
              disabled={isSubmitting}
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer la ventilation
            </Button>
          )}
          <div className="flex-1" />
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isValid || isSubmitting || isOverBudget}
          >
            {isSubmitting ? (
              <>Enregistrement...</>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Enregistrer
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
